import analyticsUtils from '@scentregroup/shared/helpers/analytics-utils'
import makeUniqueProductId from '@scentregroup/shared/helpers/make-unique-product-id'
import { IMarketplaceCartItem } from '../types/cart'
import { IMarketplaceCheckout } from '../types/marketplace-checkout'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { sortBy } from 'lodash'
import { IMarketplaceItem, IMarketplaceOrder } from '../types/order'
import { CheckoutOption, CheckoutStepOrder } from './analytics-tagging'
import { MarketplaceCoupon } from '@scentregroup/shared/types'
import {
  associatePromotionsWithCoupons,
  classifyAppliedPromotions,
} from '@scentregroup/shared/helpers/marketplace-promotions'
import { isMarketplaceCouponEligible } from './marketplace-coupon'
import {
  LegacyAnalyticsEventData,
  ObjectActionAnalyticsEvent,
  ObjectActionEventProperties,
  useAnalytics,
} from '@scentregroup/shared/analytics'
import { affiliations } from '@scentregroup/shared/helpers/analytics-utils/constants'

interface ICheckoutAnalyticsProps {
  cartId: string
  items: IMarketplaceCartItem[]
  currency: string
  step: string
  scentregroupId?: string
  checkoutOption?: CheckoutOption
  coupons?: MarketplaceCoupon[]
}

interface IItemAnalytics {
  name: string
  id: string
  uniqueId: string
  price: number
  brand: string | null
  category: string | undefined
  variant: string
  quantity: number
  dimension127: string
  dimension128: string
  dimension129: string
  dimension131?: string
  dimension132?: string
  dimension133?: string
  dimension134?: string
  dimension135?: CheckoutOption | ''
  dimension136?: string // variantGroupCode
  dimension137?: string // sku
  dimension138?: number // shopId
}

interface IUseTrackCheckoutStepParameters {
  checkout: IMarketplaceCheckout | undefined
  step: '1' | '2' | '3'
  scentregroupId: string | null | undefined
  checkoutOption?: CheckoutOption
}

const createItemsAnalytics = ({
  items,
  currency,
  checkoutOption,
  coupons,
}: {
  items: IMarketplaceCartItem[]
  currency: string
  checkoutOption?: CheckoutOption
  coupons?: MarketplaceCoupon[]
}): IItemAnalytics[] => {
  const itemsSortedByName = sortBy(items, item => item.name)

  return itemsSortedByName.map(item => {
    const {
      name,
      id,
      sku,
      listPrice,
      discountPrice,
      brand,
      category,
      optionValues,
      quantity,
      shop,
      variantGroupCode,
    } = item
    const [appliedCouponPromotions, appliedAutoPromotions] =
      classifyAppliedPromotions(
        associatePromotionsWithCoupons({
          promotions: item.promotions,
          coupons: coupons?.filter(isMarketplaceCouponEligible),
        })
      )
    const appliedCouponPromotion = appliedCouponPromotions.length
      ? appliedCouponPromotions[0]
      : undefined
    const appliedAutoPromotion = appliedAutoPromotions.length
      ? appliedAutoPromotions[0]
      : undefined

    return {
      name: name,
      id: variantGroupCode ?? sku,
      uniqueId: makeUniqueProductId(id, shop.id, sku),
      price: discountPrice || listPrice,
      brand: brand ?? '',
      category: category?.path
        .map(pathSegment => pathSegment.title)
        .join(' / '),
      variant: optionValues.map(o => o.value).join('|'),
      quantity: quantity,
      dimension127: shop.name,
      dimension128: shop.retailer?.sys.id ?? '',
      dimension129: currency,
      ...(appliedAutoPromotion && {
        coupon: `NoCoupon_${appliedAutoPromotion.campaignId}`,
        dimension131: appliedAutoPromotion.campaignId,
        dimension132: appliedAutoPromotion.title,
      }),
      ...(appliedCouponPromotion && {
        coupon: appliedCouponPromotion.couponCode,
        dimension133: appliedCouponPromotion.campaignId,
        dimension134: appliedCouponPromotion.title,
      }),
      dimension135: checkoutOption ?? '',
      dimension136: variantGroupCode,
      dimension137: sku,
      dimension138: shop.id,
    }
  })
}

const createOrderItemsAnalytics = ({
  items,
  currency,
  order,
}: {
  items: IMarketplaceItem[]
  currency: string
  order: IMarketplaceOrder
}): IItemAnalytics[] => {
  const itemsSortedByName = sortBy(items, item => item.name)

  return itemsSortedByName.map(item => {
    const {
      name,
      id,
      priceIncTax,
      quantity,
      variant: { sku, brand, category, optionValues, variantGroupCode },
      shop,
    } = item
    const [appliedCouponPromotions, appliedAutoPromotions] =
      classifyAppliedPromotions(
        associatePromotionsWithCoupons({
          promotions: item.promotions,
          coupons: order.coupons,
        })
      )
    const appliedCouponPromotion = appliedCouponPromotions.length
      ? appliedCouponPromotions[0]
      : undefined
    const appliedAutoPromotion = appliedAutoPromotions.length
      ? appliedAutoPromotions[0]
      : undefined

    return {
      name: name,
      id: variantGroupCode ?? sku,
      uniqueId: makeUniqueProductId(id, shop.id, sku),
      price: priceIncTax,
      brand: brand ?? '',
      category: category?.path
        .map(pathSegment => pathSegment.title)
        .join(' / '),
      variant: optionValues?.map(o => o.value).join('|') ?? '',
      quantity: quantity,
      dimension125: order.parcels[0].fulfilmentType,
      dimension126: order.scentregroupId ? 'loggedin' : 'guest',
      dimension127: shop.name,
      dimension128: shop.retailer?.sys.id ?? '',
      dimension129: currency,
      ...(appliedAutoPromotion && {
        coupon: `NoCoupon_${appliedAutoPromotion.campaignId}`,
        dimension131: appliedAutoPromotion.campaignId,
        dimension132: appliedAutoPromotion.title,
      }),
      ...(appliedCouponPromotion && {
        coupon: appliedCouponPromotion.couponCode,
        dimension133: appliedCouponPromotion.campaignId,
        dimension134: appliedCouponPromotion.title,
      }),
      dimension136: variantGroupCode,
      dimension137: sku,
      dimension138: shop.id,
    }
  })
}

const trackCheckoutAnalytics = ({
  items,
  currency,
  step,
  scentregroupId,
  checkoutOption: checkoutOptionProp,
  coupons,
}: ICheckoutAnalyticsProps | Record<string, never>): void => {
  const checkoutOption = getCheckoutOptionIfPayment(step, checkoutOptionProp)
  analyticsUtils.gaTagTracking({ ecommerce: null })
  analyticsUtils.gaTagTracking({
    event: analyticsUtils.events.SEND_HIT_CHECKOUT,
    ecommerce: {
      currencyCode: currency,
      checkout: {
        actionField: {
          step: step,
          option: scentregroupId ? 'loggedin' : 'guest',
        },
        products: createItemsAnalytics({
          items,
          currency,
          checkoutOption,
          coupons,
        }),
      },
    },
  })
}

/**
 * We only need checkoutOption in products analytics at Payment stage (checkout step 3)
 */
function getCheckoutOptionIfPayment(
  step: string,
  checkoutOptionProp: CheckoutOption | undefined
): CheckoutOption | undefined {
  return step === CheckoutStepOrder.PAYMENT
    ? checkoutOptionProp ?? CheckoutOption.CREDIT_CARD
    : undefined
}

export function makeAnalyticsCheckoutStartedEvent(
  cartId: string,
  cartItems: IMarketplaceCartItem[],
  coupons: MarketplaceCoupon[],
  currency: string
): Extract<
  ObjectActionAnalyticsEvent,
  { object: 'Checkout'; action: 'Started' }
> {
  const products = makeAnalyticsCheckoutStartedEventProducts(
    cartItems,
    coupons,
    currency
  )

  return {
    object: 'Checkout',
    action: 'Started',
    properties: {
      category: 'Ecommerce',
      label: 'checkout_started',
      cart_affiliation: affiliations.WESTFIELD_DIRECT_AU,
      cart_id: cartId,
      currency,
      products,
      value: products.reduce(
        (total, product) =>
          total + (product.price ? product.price : 0) * product.quantity,
        0
      ),
    },
  }
}

function makeAnalyticsCheckoutStartedEventProducts(
  cartItems: IMarketplaceCartItem[],
  coupon: MarketplaceCoupon[],
  currency: string
): ObjectActionEventProperties['Checkout']['Started']['products'] {
  return cartItems.map(item => {
    const [appliedCouponPromotions, appliedAutoPromotions] =
      classifyAppliedPromotions(
        associatePromotionsWithCoupons({
          promotions: item.promotions,
          coupons: coupon?.filter(isMarketplaceCouponEligible),
        })
      )
    const appliedCouponPromotion = appliedCouponPromotions.length
      ? appliedCouponPromotions[0]
      : undefined
    const appliedAutoPromotion = appliedAutoPromotions.length
      ? appliedAutoPromotions[0]
      : undefined

    const promotions = []
    if (appliedCouponPromotion) {
      promotions.push({
        promotion_id: appliedCouponPromotion.campaignId,
        promotion_name: appliedCouponPromotion.title,
        promotion_type: 'coupon' as const,
      })
    }
    if (appliedAutoPromotion) {
      promotions.push({
        promotion_id: appliedAutoPromotion.campaignId,
        promotion_name: appliedAutoPromotion.title,
        promotion_type: 'auto' as const,
      })
    }

    const product: ObjectActionEventProperties['Checkout']['Started']['products'][number] =
      {
        product_id: item.variantGroupCode ? item.variantGroupCode : '',
        product_variant_id: makeUniqueProductId(
          item.id,
          item.shop.id,
          item.sku
        ),
        variant: item.optionValues.map(o => o.value).join('|'),
        currency,
        price: item.discountPrice || item.listPrice,
        name: item.name,
        brand: item.brand || '',
        quantity: item.quantity,
        retailer_name: item.shop.name,
        retailer_id: item.shop.retailer?.sys.id,
        image_url: item.image?.url.replace(/^\/\//, 'https://'),
      }
    if (promotions.length !== 0) {
      product.promotions = promotions
    }

    if (item.category) {
      const maxCategoryLimit = 5
      item.category.path.forEach((singlePath, index) => {
        if (index < maxCategoryLimit && singlePath.title !== '') {
          const key = `category_${index + 1}` as const
          product[key] = singlePath.title
        }
      })
      product.category_path = item.category.path
        .map(pathSegment => pathSegment.title)
        .join(' / ')
    }

    return product
  })
}

export function makeAnalyticsOrderCompletedEventData({
  cartId,
  order,
  items,
  currency,
}: {
  cartId?: string
  order: IMarketplaceOrder
  items: IMarketplaceItem[]
  currency: string
}): LegacyAnalyticsEventData {
  const { shippingAddress } = order
  return {
    event: analyticsUtils.events.PURCHASE,
    cartId,
    ecommerce: {
      currencyCode: currency,
      affiliation: analyticsUtils.affiliations.WESTFIELD_DIRECT_AU,
      purchase: {
        actionField: {
          id: order.orderReference,
          affiliation: analyticsUtils.affiliations.WESTFIELD_DIRECT_AU,
          revenue: String(order.totalIncTax),
          tax: String(order.totalTax),
          shipping: String(order.parcels[0].shippingCost),
          coupon: order.coupons?.[0]?.code,
        },
        products: createOrderItemsAnalytics({
          items,
          currency,
          order,
        }),
        deliveryType: order.parcels[0].fulfilmentType,
        collectLocation: order.parcels[0].fulfilmentLocation,
        paymentMethod: order.payment.cardType,
        shippingAddress: shippingAddress
          ? {
              address1: shippingAddress.address1,
              suburb: shippingAddress.suburb,
              state: shippingAddress.state,
              postCode: shippingAddress.postCode,
              country: shippingAddress.country,
            }
          : undefined,
        billingAddress: {
          address1: order.billingAddress.address1,
          suburb: order.billingAddress.suburb,
          state: order.billingAddress.state,
          postCode: order.billingAddress.postCode,
          country: order.billingAddress.country,
        },
      },
    },
  }
}

function makeCheckoutAnalyticsEventData({
  cartId,
  items,
  currency,
  step,
  scentregroupId,
  checkoutOption: checkoutOptionProp,
  coupons,
}: ICheckoutAnalyticsProps | Record<string, never>): LegacyAnalyticsEventData {
  const checkoutOption = getCheckoutOptionIfPayment(step, checkoutOptionProp)
  return {
    event: analyticsUtils.events.SEND_HIT_CHECKOUT,
    cartId,
    ecommerce: {
      currencyCode: currency,
      checkout: {
        actionField: {
          step: step,
          option: scentregroupId ? 'loggedin' : 'guest',
        },
        products: createItemsAnalytics({
          items,
          currency,
          checkoutOption,
          coupons,
        }),
      },
    },
  }
}

export const useCreateCheckoutAnalytics = (
  event: ICheckoutAnalyticsProps | Record<string, never> // cannot be null, useDeepCompareEffect() requires non-primitive values as dependencies
): void => {
  const { trackEvent } = useAnalytics()
  useDeepCompareEffect(() => {
    if (event && event.items) {
      trackEvent({
        type: 'legacy',
        data: makeCheckoutAnalyticsEventData(event),
      })
      trackCheckoutOption({
        checkoutOption: event.checkoutOption,
        step: event.step,
      })
    }
  }, [event])
}

/**
 * This custom hook checks if there are items in the checkout,
 * then eventually calls the trackCheckoutAnalytics to push sendhitCheckout event to dataLayer.
 */

export function useTrackCheckoutStep({
  checkout,
  step,
  scentregroupId,
}: IUseTrackCheckoutStepParameters): void {
  const event = generateCheckoutAnalyticsEvent({
    checkout,
    step,
    scentregroupId,
  })

  useCreateCheckoutAnalytics(event)
}

// cannot return null, useDeepCompareEffect() requires non-primitive values as dependencies
function generateCheckoutAnalyticsEvent({
  checkout,
  step,
  scentregroupId,
  checkoutOption,
}: IUseTrackCheckoutStepParameters):
  | ICheckoutAnalyticsProps
  | Record<string, never> {
  if (!checkout) {
    return _generateCheckoutAnalyticsEventDegenerateValue
  }

  const checkoutItems = checkout?.parcels.flatMap(parcel => parcel.items)
  if (!checkoutItems) {
    return _generateCheckoutAnalyticsEventDegenerateValue
  }

  return {
    cartId: checkout.id,
    step,
    scentregroupId: scentregroupId ?? undefined,
    currency: checkout.currency,
    items: checkoutItems,
    checkoutOption,
    coupons: checkout.coupons,
  }
}

const _generateCheckoutAnalyticsEventDegenerateValue = {}

/**
 * Push "sendhitCheckout" and "checkoutOpion" events to dataLayer when paying with other payment methods
 * (instead of useTrackCheckoutStep for credit cards)
 */
export function trackCheckoutStepForOtherPaymentMethods({
  checkout,
  step,
  scentregroupId,
  checkoutOption,
}: IUseTrackCheckoutStepParameters): void {
  const event = generateCheckoutAnalyticsEvent({
    checkout,
    step,
    scentregroupId,
    checkoutOption,
  })
  // When Apple pay popup is open, we don't need to track items change, so we can fire analytics event now
  if (event?.items) {
    trackCheckoutAnalytics(event)
    trackCheckoutOption({ checkoutOption, step })
  }
  return
}

const trackCheckoutOption = ({
  checkoutOption,
  step,
}: {
  checkoutOption?: CheckoutOption
  step: string
}): void => {
  if (step === CheckoutStepOrder.PAYMENT) {
    analyticsUtils.gaTagTracking({ ecommerce: null })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.CHECKOUT_OPTION,
      ecommerce: {
        checkout_option: {
          actionField: {
            step: step,
            option: checkoutOption ?? CheckoutOption.CREDIT_CARD,
          },
        },
      },
    })
  }
}
